import React, { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  MessageSquare,
  GraduationCap,
  Sparkles,
  ExternalLink,
  Check,
  RotateCcw,
} from 'lucide-react';
import {
  EDUCATION_TOPICS,
  CIGAR_FINDER_QUESTIONS,
  generateRecommendations,
  CigarSuggestion,
  EducationTopic,
} from '../data/cigarEducation';
import { UserPreferences, TastingNote } from '../types';
import {
  VitolaChart,
  CigarAnatomyDiagram,
  WrapperSwatches,
  RingGaugeExplainer,
  CuttingMethodsDiagram,
  LightingStepsDiagram,
} from '../components/CigarIllustrations';

interface CigarAdvisorHubProps {
  userPreferences?: UserPreferences;
  journalNotes: TastingNote[];
  festivalName?: string;
  onBack: () => void;
  onSaveQuestion?: (question: string) => void;
}

type AdvisorMode = 'hub' | 'education' | 'topic' | 'finder' | 'results' | 'festival';

const CigarAdvisorHub: React.FC<CigarAdvisorHubProps> = ({
  userPreferences,
  journalNotes,
  festivalName,
  onBack,
  onSaveQuestion,
}) => {
  const [mode, setMode] = useState<AdvisorMode>('hub');
  const [selectedTopic, setSelectedTopic] = useState<EducationTopic | null>(null);
  const [quizStep, setQuizStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<Record<string, string | string[]>>({});
  const [recommendations, setRecommendations] = useState<CigarSuggestion[]>([]);

  // Festival questions generator (existing feature)
  const generateFestivalQuestions = () => {
    const questions: { category: string; question: string; color: string }[] = [];
    const prefs = userPreferences;

    // Strength-based question
    if (prefs?.strength) {
      const strengthMap: Record<string, string> = {
        mild: "I prefer milder cigars. What's your smoothest, most approachable stick?",
        medium: "I enjoy medium-bodied cigars. What would you recommend that's balanced but interesting?",
        'medium-full': "I like cigars with some punch. What's your boldest offering that won't knock me out?",
        full: "I love full-bodied cigars. What's your most powerful, complex smoke?",
      };
      questions.push({
        category: 'STRENGTH',
        question: strengthMap[prefs.strength] || "What strength levels do you offer?",
        color: 'amber',
      });
    }

    // Flavor-based question
    if (prefs?.flavors && prefs.flavors.length > 0) {
      const flavors = prefs.flavors.slice(0, 2).join(' and ');
      questions.push({
        category: 'FLAVOR',
        question: `I'm drawn to ${flavors} flavors. Which of your cigars would highlight those notes?`,
        color: 'emerald',
      });
    }

    // Experience-based question
    if (journalNotes.length > 0) {
      const topBrands = journalNotes
        .filter(n => n.overallScore && n.overallScore >= 85)
        .map(n => n.brand)
        .filter((b, i, arr) => arr.indexOf(b) === i)
        .slice(0, 2);

      if (topBrands.length > 0) {
        questions.push({
          category: 'COMPARISON',
          question: `I really enjoy ${topBrands.join(' and ')}. Do you have anything in a similar profile?`,
          color: 'blue',
        });
      }
    }

    // General discovery question
    questions.push({
      category: 'DISCOVERY',
      question: "What's your hidden gem - something most people overlook but you personally love?",
      color: 'purple',
    });

    // Value question
    questions.push({
      category: 'VALUE',
      question: "What's your best everyday smoke? Something reliable that won't break the bank?",
      color: 'rose',
    });

    return questions;
  };

  const handleQuizAnswer = (questionId: string, value: string | string[]) => {
    const newAnswers = { ...quizAnswers, [questionId]: value };
    setQuizAnswers(newAnswers);

    // Move to next step or generate results
    if (quizStep < CIGAR_FINDER_QUESTIONS.length - 1) {
      setQuizStep(quizStep + 1);
    } else {
      const recs = generateRecommendations(newAnswers);
      setRecommendations(recs);
      setMode('results');
    }
  };

  const resetQuiz = () => {
    setQuizStep(0);
    setQuizAnswers({});
    setRecommendations([]);
    setMode('finder');
  };

  // Hub view - main menu
  if (mode === 'hub') {
    return (
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
          <div className="flex items-center gap-2 px-4 py-3">
            <button onClick={onBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-lg font-semibold text-white">Cigar Advisor</h1>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Hero section */}
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Your Cigar Guide</h2>
            <p className="text-stone-400">Learn, discover, and find your perfect smoke</p>
          </div>

          {/* Main options */}
          <div className="space-y-3">
            {/* Cigar Finder */}
            <button
              onClick={() => setMode('finder')}
              className="w-full p-4 bg-gradient-to-r from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-xl text-left hover:from-amber-500/30 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <Search className="w-6 h-6 text-amber-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">Find Your Cigar</h3>
                  <p className="text-sm text-stone-400">Answer a few questions, get personalized picks</p>
                </div>
                <ChevronRight className="w-5 h-5 text-amber-500" />
              </div>
            </button>

            {/* Cigar 101 Education */}
            <button
              onClick={() => setMode('education')}
              className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-left hover:bg-white/10 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-emerald-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white">Cigar 101</h3>
                  <p className="text-sm text-stone-400">Ring gauge, wrappers, cutting, and more</p>
                </div>
                <ChevronRight className="w-5 h-5 text-stone-500" />
              </div>
            </button>

            {/* Festival Questions */}
            {festivalName && (
              <button
                onClick={() => setMode('festival')}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-left hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-blue-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">Festival Questions</h3>
                    <p className="text-sm text-stone-400">Questions to ask vendors at {festivalName}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-stone-500" />
                </div>
              </button>
            )}
          </div>

          {/* Quick topics */}
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-stone-400 uppercase tracking-wide mb-3">
              Quick Learn
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {EDUCATION_TOPICS.slice(0, 4).map((topic) => (
                <button
                  key={topic.id}
                  onClick={() => {
                    setSelectedTopic(topic);
                    setMode('topic');
                  }}
                  className="p-3 bg-white/5 rounded-lg text-left hover:bg-white/10 transition-colors"
                >
                  <div className="text-xl mb-1">{topic.icon}</div>
                  <div className="text-sm font-medium text-white truncate">{topic.title}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Education view - topics list
  if (mode === 'education') {
    return (
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
          <div className="flex items-center gap-2 px-4 py-3">
            <button onClick={() => setMode('hub')} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-lg font-semibold text-white">Cigar 101</h1>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Topics list */}
          <div className="space-y-3">
            {EDUCATION_TOPICS.map((topic) => (
              <button
                key={topic.id}
                onClick={() => {
                  setSelectedTopic(topic);
                  setMode('topic');
                }}
                className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-left hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="text-2xl">{topic.icon}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">{topic.title}</h3>
                    <p className="text-sm text-stone-400">{topic.summary}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-stone-500" />
                </div>
              </button>
            ))}
          </div>

          {/* Visual Vitola Chart */}
          <div>
            <h3 className="text-sm font-semibold text-stone-400 uppercase tracking-wide mb-3">
              Vitola (Size) Reference
            </h3>
            <VitolaChart />
          </div>

          {/* Visual Wrapper Swatches */}
          <div>
            <h3 className="text-sm font-semibold text-stone-400 uppercase tracking-wide mb-3">
              Wrapper Color Guide
            </h3>
            <WrapperSwatches />
          </div>
        </div>
      </div>
    );
  }

  // Topic detail view
  if (mode === 'topic' && selectedTopic) {
    // Render topic-specific illustration
    const renderTopicIllustration = () => {
      switch (selectedTopic.id) {
        case 'ring-gauge':
          return (
            <div className="mb-6 p-4 bg-white/5 rounded-xl">
              <RingGaugeExplainer />
            </div>
          );
        case 'construction':
          return (
            <div className="mb-6 p-4 bg-white/5 rounded-xl">
              <h4 className="text-sm font-semibold text-stone-400 mb-3">Cigar Cross-Section</h4>
              <CigarAnatomyDiagram />
            </div>
          );
        case 'cutting':
          return (
            <div className="mb-6 p-4 bg-white/5 rounded-xl">
              <h4 className="text-sm font-semibold text-stone-400 mb-3">Cutting Methods</h4>
              <CuttingMethodsDiagram />
            </div>
          );
        case 'lighting':
          return (
            <div className="mb-6 p-4 bg-white/5 rounded-xl">
              <h4 className="text-sm font-semibold text-stone-400 mb-3">Lighting Steps</h4>
              <LightingStepsDiagram />
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
          <div className="flex items-center gap-2 px-4 py-3">
            <button onClick={() => setMode('education')} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-lg font-semibold text-white">{selectedTopic.title}</h1>
          </div>
        </div>

        <div className="p-4">
          <div className="text-4xl mb-4">{selectedTopic.icon}</div>

          {/* Topic-specific illustration */}
          {renderTopicIllustration()}

          <div className="prose prose-invert prose-sm max-w-none">
            {selectedTopic.content.split('\n\n').map((paragraph, i) => (
              <div key={i} className="mb-4">
                {paragraph.startsWith('**') ? (
                  <h3 className="text-amber-500 font-semibold mb-2">
                    {paragraph.replace(/\*\*/g, '')}
                  </h3>
                ) : paragraph.startsWith('- ') ? (
                  <ul className="list-disc list-inside text-stone-300 space-y-1">
                    {paragraph.split('\n').map((item, j) => (
                      <li key={j}>{item.replace('- ', '')}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-stone-300">{paragraph}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Cigar Finder Quiz
  if (mode === 'finder') {
    const currentQuestion = CIGAR_FINDER_QUESTIONS[quizStep];
    const isMultiSelect = currentQuestion.id === 'flavors';
    const selectedFlavors = (quizAnswers.flavors as string[]) || [];

    return (
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
          <div className="flex items-center gap-2 px-4 py-3">
            <button
              onClick={() => {
                if (quizStep === 0) {
                  setMode('hub');
                } else {
                  setQuizStep(quizStep - 1);
                }
              }}
              className="p-2 -ml-2 hover:bg-white/10 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-lg font-semibold text-white">Find Your Cigar</h1>
          </div>
          {/* Progress bar */}
          <div className="px-4 pb-3">
            <div className="flex gap-1">
              {CIGAR_FINDER_QUESTIONS.map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 h-1 rounded-full ${
                    i <= quizStep ? 'bg-amber-500' : 'bg-stone-700'
                  }`}
                />
              ))}
            </div>
            <div className="text-xs text-stone-500 mt-1">
              Step {quizStep + 1} of {CIGAR_FINDER_QUESTIONS.length}
            </div>
          </div>
        </div>

        <div className="p-4">
          <h2 className="text-xl font-bold text-white mb-6">{currentQuestion.question}</h2>

          <div className="space-y-3">
            {currentQuestion.options.map((option) => {
              const isSelected = isMultiSelect
                ? selectedFlavors.includes(option.value)
                : quizAnswers[currentQuestion.id] === option.value;

              return (
                <button
                  key={option.value}
                  onClick={() => {
                    if (isMultiSelect) {
                      let newFlavors: string[];
                      if (selectedFlavors.includes(option.value)) {
                        newFlavors = selectedFlavors.filter((f) => f !== option.value);
                      } else if (selectedFlavors.length < 3) {
                        newFlavors = [...selectedFlavors, option.value];
                      } else {
                        return; // Max 3 selected
                      }
                      setQuizAnswers({ ...quizAnswers, flavors: newFlavors });
                    } else {
                      handleQuizAnswer(currentQuestion.id, option.value);
                    }
                  }}
                  className={`w-full p-4 rounded-xl text-left transition-colors ${
                    isSelected
                      ? 'bg-amber-500/20 border-2 border-amber-500'
                      : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-white">{option.label}</div>
                      {option.description && (
                        <div className="text-sm text-stone-400">{option.description}</div>
                      )}
                    </div>
                    {isSelected && <Check className="w-5 h-5 text-amber-500" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Continue button for multi-select */}
          {isMultiSelect && (
            <button
              onClick={() => {
                if (selectedFlavors.length > 0) {
                  handleQuizAnswer('flavors', selectedFlavors);
                }
              }}
              disabled={selectedFlavors.length === 0}
              className="w-full mt-6 py-3 bg-amber-500 text-stone-900 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue ({selectedFlavors.length}/3 selected)
            </button>
          )}
        </div>
      </div>
    );
  }

  // Results view
  if (mode === 'results') {
    return (
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
          <div className="flex items-center gap-2 px-4 py-3">
            <button onClick={() => setMode('hub')} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-lg font-semibold text-white">Your Recommendations</h1>
          </div>
        </div>

        <div className="p-4">
          <div className="text-center mb-6">
            <Sparkles className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white">Based on your preferences</h2>
            <p className="text-stone-400 text-sm mt-1">
              We found {recommendations.length} cigars you might love
            </p>
          </div>

          <div className="space-y-4">
            {recommendations.map((cigar, index) => (
              <div
                key={index}
                className="p-4 bg-white/5 border border-white/10 rounded-xl"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold text-white">{cigar.name}</h3>
                    <p className="text-sm text-stone-500">{cigar.brand}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-amber-500 font-medium">{cigar.price}</div>
                    <div className="text-xs text-stone-500">{cigar.strength}</div>
                  </div>
                </div>
                <p className="text-sm text-stone-300 mb-2">{cigar.description}</p>
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2 py-1 bg-amber-500/20 text-amber-500 rounded">
                    {cigar.wrapper}
                  </span>
                </div>
                <p className="text-xs text-emerald-500 mt-2">
                  <Check className="w-3 h-3 inline mr-1" />
                  {cigar.matchReason}
                </p>
              </div>
            ))}
          </div>

          {/* Search online CTA */}
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl">
            <h3 className="font-medium text-white mb-2">Want to buy?</h3>
            <p className="text-sm text-stone-400 mb-3">
              Search for these cigars at your favorite retailer
            </p>
            <button
              onClick={() => {
                const query = recommendations[0]?.name || 'premium cigars';
                window.open(`https://www.google.com/search?q=${encodeURIComponent(query + ' cigars buy online')}`, '_blank');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium"
            >
              Search Online
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>

          {/* Retake quiz */}
          <button
            onClick={resetQuiz}
            className="w-full mt-4 py-3 bg-white/5 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Retake Quiz
          </button>
        </div>
      </div>
    );
  }

  // Festival questions view
  if (mode === 'festival') {
    const questions = generateFestivalQuestions();

    return (
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="sticky top-0 z-10 bg-stone-900/90 backdrop-blur-lg border-b border-white/10">
          <div className="flex items-center gap-2 px-4 py-3">
            <button onClick={() => setMode('hub')} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-lg font-semibold text-white">Festival Questions</h1>
          </div>
        </div>

        <div className="p-4">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-white">Your Questions for {festivalName}</h2>
            <p className="text-stone-400 text-sm mt-1">
              Personalized based on your taste profile
            </p>
          </div>

          <div className="space-y-3">
            {questions.map((q, index) => (
              <div
                key={index}
                className="p-4 bg-white/5 border border-white/10 rounded-xl"
              >
                <div className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-2 bg-${q.color}-500/20 text-${q.color}-500`}>
                  {q.category}
                </div>
                <p className="text-white">{q.question}</p>
                {onSaveQuestion && (
                  <button
                    onClick={() => onSaveQuestion(q.question)}
                    className="mt-2 text-xs text-amber-500 hover:text-amber-400"
                  >
                    Save question
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CigarAdvisorHub;
